import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  GridItem,
  Text,
  Table,
  Tr,
  Thead,
  Th,
  Tbody,
  Td,
  TableContainer,
  Button,
  Flex,
  Image,
  Checkbox,
  useToast,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { completeDeliveryGroup } from "../controller/api";
import { markBusy } from "../utils/appBusy";
import { drawTimestampWatermark, toFileNameTimestamp, toSeoulIsoString } from "../utils/captureStamp";

// Global callback function that Android will call
window.handleCapturedImage = function (imageUri) {
  // We need to convert the URI to a file and update component state
  // This will be called by Android after photo capture

  // Get the current component instance
  const currentComponent = window.currentDeliveryDetailInstance;
  if (currentComponent) {
    currentComponent.processAndroidImage(imageUri);
  }
};

// 사용하지 않게 된 카메라 스트림을 확실히 끈다.
// 끄지 않고 남기면 다음 촬영 시도가 NotReadableError로 실패한다.
function stopMediaStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

const DeliveryDetail = ({
  delivery_id,
  image,
  phone,
  address,
  content,
  password,
  note,
  order_items,
  onDelivered,
  onDeliveryFailed,
}) => {
  const [capturedImage, setCapturedImage] = useState(null); // 미리보기용
  const [imageFile, setImageFile] = useState(null); // 전송용
  // 촬영 시각. 워터마크로 사진에 새기고 같은 값을 서버로 함께 보낸다.
  const [capturedAt, setCapturedAt] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isOrderConfirmed, setIsOrderConfirmed] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false); // 카메라 모달 상태
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false); // 사진 전체화면 보기

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // 카메라 스트림 참조
  // 카메라 열기 시도를 식별하는 토큰. getUserMedia를 기다리는 동안 사용자가
  // 모달을 닫으면 값이 바뀌어, 뒤늦게 도착한 스트림이 화면을 되살리지 않는다.
  const cameraSessionRef = useRef(0);
  // 전송에 성공하면 미리보기 blob URL의 소유권이 부모(DeliveryList)로 넘어간다.
  // 넘긴 뒤에도 정리 단계에서 revoke하면 부모가 들고 있는 URL이 죽는다.
  const urlTransferredRef = useRef(false);
  const toast = useToast();

  // 카메라 스트림 정리 함수
  const stopCamera = useCallback(() => {
    // 진행 중인 카메라 열기 시도를 무효화한다
    cameraSessionRef.current += 1;

    if (streamRef.current) {
      stopMediaStream(streamRef.current);
      streamRef.current = null;
    }
  }, []);

  // URL 정리 함수
  const cleanupImageUrl = useCallback((url) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    if (image) {
      setIsSubmitted(true);
      setIsOrderConfirmed(true);
    }
  }, [image]);

  // 언마운트 시 카메라 정리.
  // 아래 전역 등록과 분리해 두어야 그쪽 의존성이 바뀔 때 촬영 중인 스트림이
  // 함께 꺼지지 않는다.
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // processAndroidImage는 전역에 등록되므로 렌더마다 다시 만들지 않는다.
  // 최신 capturedImage는 의존성 대신 ref로 읽는다.
  const capturedImageRef = useRef(null);

  useEffect(() => {
    capturedImageRef.current = capturedImage;
  }, [capturedImage]);

  useEffect(() => {
    // Store reference to current component instance for Android callback
    window.currentDeliveryDetailInstance = {
      processAndroidImage: async (imageUri) => {
        try {
          console.log("🤖 Android 네이티브 카메라에서 이미지 수신:", imageUri);

          // 기존 URL 정리
          cleanupImageUrl(capturedImageRef.current);

          // Canvas를 사용해서 직접 압축 (browser-image-compression 대신)
          const response = await fetch(imageUri);
          const blob = await response.blob();

          console.log(
            `🤖 Android 원본 이미지 크기: ${(blob.size / 1024).toFixed(0)}KB`
          );

          // 캔버스로 압축
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          // 이 모듈은 Chakra UI의 Image를 import하고 있어 전역 Image 생성자가 가려진다
          const img = new window.Image();

          img.onload = () => {
            // 최대 600px로 제한
            let { width, height } = img;
            if (width > 600 || height > 600) {
              if (width > height) {
                height = (height * 600) / width;
                width = 600;
              } else {
                width = (width * 600) / height;
                height = 600;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // 촬영 시각을 사진에 새긴다 (한국시간)
            const shotAt = new Date();
            drawTimestampWatermark(ctx, canvas, shotAt);

            canvas.toBlob(
              (compressedBlob) => {
                const compressedUrl = URL.createObjectURL(compressedBlob);
                setCapturedImage(compressedUrl);

                const file = new File([compressedBlob], `delivery_${toFileNameTimestamp(shotAt)}.jpg`, {
                  type: "image/jpeg",
                });
                setImageFile(file);
                setCapturedAt(toSeoulIsoString(shotAt));

                console.log(
                  `🤖 Android 이미지 압축 완료: ${(blob.size / 1024).toFixed(
                    0
                  )}KB → ${(file.size / 1024).toFixed(0)}KB`
                );

                toast({
                  title: "Android 네이티브 카메라",
                  description: `이미지 압축 완료 (${(file.size / 1024).toFixed(
                    0
                  )}KB)`,
                  status: "success",
                  duration: 2000,
                });
              },
              "image/jpeg",
              0.7
            );
          };

          img.src = imageUri;
        } catch (err) {
          console.error("🤖 Android 카메라 이미지 처리 오류:", err);
          setCapturedImage(null);
        }
      },
    };

    return () => {
      window.currentDeliveryDetailInstance = null;
    };
  }, [cleanupImageUrl, toast]);

  // 컴포넌트 언마운트 시 URL 정리
  useEffect(() => {
    return () => {
      // 부모에게 넘긴 URL은 이제 부모가 화면에 쓰고 있으므로 해제하지 않는다
      if (urlTransferredRef.current) {
        return;
      }
      if (capturedImage && capturedImage.startsWith("blob:")) {
        cleanupImageUrl(capturedImage);
      }
    };
  }, [capturedImage, cleanupImageUrl]);

  // 촬영 중이거나, 촬영했지만 아직 전송하지 않은 사진이 있거나, 업로드가 진행
  // 중이면 서비스워커 업데이트로 인한 새로고침을 미루게 한다. 이 시점에
  // 새로고침되면 기사님이 찍은 사진이 사라진다.
  const hasUnsentPhoto = Boolean(capturedImage) && !isSubmitted;

  useEffect(() => {
    if (!isCameraOpen && !hasUnsentPhoto && !isUploading) {
      return undefined;
    }
    return markBusy();
  }, [isCameraOpen, hasUnsentPhoto, isUploading]);

  // 인페이지 카메라 시작 (모든 플랫폼 지원)
  const startCamera = useCallback(async () => {
    // 이번 시도를 식별할 토큰. stopCamera가 호출되면 값이 바뀐다.
    const sessionId = cameraSessionRef.current + 1;
    cameraSessionRef.current = sessionId;

    // 이번 시도로 얻은 스트림이 더 이상 필요 없어졌는지 확인한다
    const isCancelled = () => cameraSessionRef.current !== sessionId;

    try {
      console.log("📱 카메라 시작 시도 중...");

      // Android인지 확인
      const isAndroid = /Android/.test(navigator.userAgent);
      console.log("🤖 Android 감지:", isAndroid);

      // 기본 제약 조건
      let constraints = {
        video: {
          facingMode: "environment",
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
        },
        audio: false,
      };

      // Android일 경우 다른 설정 시도
      if (isAndroid) {
        console.log("🤖 Android용 카메라 설정 적용");
        constraints = {
          video: {
            facingMode: { exact: "environment" },
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            aspectRatio: { ideal: 16 / 9 },
          },
          audio: false,
        };
      }

      console.log("� 카메라 제약조건:", JSON.stringify(constraints, null, 2));

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log("✅ 카메라 스트림 획득 성공");
      console.log("� 스트림 정보:", {
        active: stream.active,
        tracks: stream.getVideoTracks().length,
        trackSettings: stream.getVideoTracks()[0]?.getSettings(),
      });

      // 스트림을 먼저 ref에 보관한다. 아래 await 도중 예외가 나거나 사용자가
      // 취소해도 스트림이 지역 변수에 갇혀 카메라가 켜진 채 남지 않게 한다.
      streamRef.current = stream;

      if (isCancelled()) {
        stopMediaStream(stream);
        streamRef.current = null;
        return;
      }

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        console.log("📺 비디오 엘리먼트에 스트림 할당");

        // Android일 경우 추가 처리
        if (isAndroid) {
          video.setAttribute("webkit-playsinline", "true");
          video.setAttribute("playsinline", "true");

          // 비디오 시작을 기다림.
          // 메타데이터 이벤트가 오지 않는 기기가 있어 타임아웃을 함께 둔다.
          await new Promise((resolve) => {
            const timer = setTimeout(resolve, 3000);
            video.onloadedmetadata = () => {
              clearTimeout(timer);
              console.log("🤖 Android 비디오 메타데이터 로드됨");
              resolve();
            };
          });

          if (isCancelled()) {
            stopMediaStream(stream);
            streamRef.current = null;
            return;
          }

          // 재생 강제 시도
          try {
            await video.play();
            console.log("🤖 Android 비디오 재생 성공");
          } catch (playError) {
            console.warn("⚠️ Android 비디오 재생 실패:", playError);
          }
        }
      }

      if (isCancelled()) {
        stopMediaStream(stream);
        streamRef.current = null;
        return;
      }

      setIsCameraOpen(true);
    } catch (err) {
      console.error("❌ 카메라 접근 실패:", err);
      console.error("❌ 오류 이름:", err.name);
      console.error("❌ 오류 메시지:", err.message);

      // Android에서 실패했을 경우 더 간단한 설정으로 재시도
      if (
        /Android/.test(navigator.userAgent) &&
        err.name === "OverconstrainedError"
      ) {
        console.log("🤖 Android 재시도: 더 간단한 설정");
        try {
          const simpleConstraints = {
            video: {
              facingMode: "environment",
            },
            audio: false,
          };

          const stream = await navigator.mediaDevices.getUserMedia(
            simpleConstraints
          );
          streamRef.current = stream;

          if (isCancelled()) {
            stopMediaStream(stream);
            streamRef.current = null;
            return;
          }

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          setIsCameraOpen(true);
          console.log("✅ Android 재시도 성공");
          return;
        } catch (retryErr) {
          console.error("❌ Android 재시도도 실패:", retryErr);
        }
      }

      // 실패했으면 모달을 닫는다. 열어두면 기사님이 검은 화면에 갇히고
      // 촬영 버튼은 videoWidth가 0이라 아무 반응 없이 무시된다.
      if (streamRef.current) {
        stopMediaStream(streamRef.current);
        streamRef.current = null;
      }
      setIsCameraOpen(false);

      toast({
        title: "카메라 접근 실패",
        description: `카메라에 접근할 수 없습니다: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // 사진 촬영 (모든 플랫폼 지원)
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    // 플랫폼 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const platform = isIOS
      ? "🍎 iOS"
      : isAndroid
      ? "🤖 Android"
      : "💻 데스크톱";

    console.log(`📱 ${platform} 인페이지 카메라에서 사진 촬영`);

    // 기존 이미지 URL 정리
    if (capturedImage && capturedImage.startsWith("blob:")) {
      cleanupImageUrl(capturedImage);
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // 스트림이 아직 준비되지 않으면 videoWidth/Height가 0이라 빈 캔버스가 만들어지고
    // toBlob이 null을 넘겨 아무 일도 일어나지 않는다. 명확히 알려주고 중단한다.
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    if (!videoWidth || !videoHeight) {
      toast({
        title: "카메라가 아직 준비되지 않았습니다",
        description: "잠시 후 다시 촬영해주세요",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    // 플랫폼별 최적화된 크기 설정
    let maxSize = 600;
    if (isIOS) {
      maxSize = 480; // iOS는 더 작게
    }

    // 가로/세로를 각각 잘라내면 16:9 화면이 정사각형으로 찌그러진다.
    // 두 축에 같은 축소 비율을 적용해 원본 비율을 유지한다.
    const scale = Math.min(1, maxSize / Math.max(videoWidth, videoHeight));
    canvas.width = Math.round(videoWidth * scale);
    canvas.height = Math.round(videoHeight * scale);

    // 비디오 프레임을 캔버스에 그리기
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // 촬영 시각을 사진에 새긴다 (한국시간)
    const shotAt = new Date();
    drawTimestampWatermark(ctx, canvas, shotAt);

    // 플랫폼별 압축 품질 조정
    const quality = isIOS ? 0.5 : 0.6; // iOS는 더 높은 압축

    // 캔버스를 blob으로 변환 (압축 적용)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // 미리보기 URL 생성
          const imageUrl = URL.createObjectURL(blob);
          setCapturedImage(imageUrl);

          // File 객체 생성
          const file = new File([blob], `delivery_${toFileNameTimestamp(shotAt)}.jpg`, {
            type: "image/jpeg",
            lastModified: shotAt.getTime(),
          });

          setImageFile(file);
          setCapturedAt(toSeoulIsoString(shotAt));

          console.log(
            `📱 ${platform} 이미지 생성 완료: ${(file.size / 1024).toFixed(
              0
            )}KB`
          );

          toast({
            title: `${platform} 인페이지 카메라`,
            description: `사진 촬영 완료 (${(file.size / 1024).toFixed(0)}KB)`,
            status: "success",
            duration: 2000,
          });

          // 카메라 종료
          stopCamera();
          setIsCameraOpen(false);
        }
      },
      "image/jpeg",
      quality
    );
  };

  // 카메라 버튼 클릭 → 인페이지 카메라 우선 사용
  const handleCameraButtonClick = () => {
    // 브라우저 지원 여부 안전하게 체크
    console.log("🔍 카메라 지원 여부 체크:");
    console.log("- navigator:", !!navigator);
    console.log("- navigator.mediaDevices:", !!navigator.mediaDevices);
    console.log(
      "- getUserMedia:",
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    );
    console.log(
      "- Android:",
      !!(window.Android && typeof window.Android.takePhoto === "function")
    );

    // 모든 플랫폼에서 인페이지 카메라 우선 사용
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      console.log("📱 인페이지 카메라 사용 (모든 플랫폼)");
      toast({
        title: "인페이지 카메라",
        description: "브라우저 내장 카메라를 실행합니다",
        status: "info",
        duration: 1000,
      });
      setIsCameraOpen(true);
      setTimeout(() => startCamera(), 100); // 모달이 열린 후 카메라 시작
    }
    // Android 네이티브 카메라 (대체 옵션)
    else if (window.Android && typeof window.Android.takePhoto === "function") {
      console.log("🤖 Android 네이티브 카메라 사용 (대체)");
      toast({
        title: "Android 네이티브 카메라",
        description: "네이티브 카메라를 실행합니다",
        status: "info",
        duration: 1000,
      });
      window.Android.takePhoto("handleCapturedImage");
    }
    // 마지막 대안으로 파일 입력
    else {
      console.log("📁 파일 선택 사용 (카메라 미지원)");
      toast({
        title: "파일 선택",
        description: "갤러리에서 이미지를 선택합니다",
        status: "info",
        duration: 1000,
      });
      fileInputRef.current?.click();
    }
  };

  // 파일 입력용 핸들러 (대체 수단)
  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // 파일 크기 체크
      if (file.size > 3 * 1024 * 1024) {
        toast({
          title: "파일이 너무 큽니다",
          description: "3MB 이하의 이미지를 선택해주세요",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // 기존 이미지 URL 정리. 상태도 함께 비워야 새 이미지 로딩에 실패했을 때
      // 이미 해제된 URL이 미리보기에 깨진 채로 남지 않는다.
      if (capturedImage && capturedImage.startsWith("blob:")) {
        cleanupImageUrl(capturedImage);
      }
      setCapturedImage(null);
      setImageFile(null);

      // 캔버스를 사용한 압축 (browser-image-compression 대신)
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        // 이 모듈은 Chakra UI의 Image를 import하고 있어 전역 Image 생성자가 가려진다
        const img = new window.Image();

        img.onload = () => {
          // 최대 600px로 제한
          let { width, height } = img;
          if (width > 600 || height > 600) {
            if (width > height) {
              height = (height * 600) / width;
              width = 600;
            } else {
              width = (width * 600) / height;
              height = 600;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // 촬영(선택) 시각을 사진에 새긴다 (한국시간)
          const shotAt = new Date();
          drawTimestampWatermark(ctx, canvas, shotAt);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], `delivery_${toFileNameTimestamp(shotAt)}.jpg`, {
                type: "image/jpeg",
                lastModified: shotAt.getTime(),
              });

              const imageUrl = URL.createObjectURL(compressedFile);
              setCapturedImage(imageUrl);
              setImageFile(compressedFile);
              setCapturedAt(toSeoulIsoString(shotAt));

              console.log(
                `파일 압축: ${(file.size / 1024).toFixed(0)}KB → ${(
                  compressedFile.size / 1024
                ).toFixed(0)}KB`
              );

              // 임시 URL 해제
              URL.revokeObjectURL(img.src);
            },
            "image/jpeg",
            0.6
          );
        };

        const tempUrl = URL.createObjectURL(file);

        // 디코딩 실패는 비동기로 발생하므로 아래 try/catch가 잡지 못한다.
        // onerror가 없으면 미리보기도 안내도 없이 조용히 무시된다.
        img.onerror = () => {
          URL.revokeObjectURL(tempUrl);
          toast({
            title: "이미지를 읽을 수 없습니다",
            description: "다른 사진을 선택해주세요",
            status: "error",
            duration: 3000,
          });
        };

        img.src = tempUrl;
      } catch (error) {
        console.error("이미지 처리 오류:", error);
        const imageUrl = URL.createObjectURL(file);
        setCapturedImage(imageUrl);
        setImageFile(file);
      }
    }
  };

  // 전송 처리 함수
  // 배송묶음 단위로 사진을 한 번만 올린다. 묶음에 연결된 주문들은 서버에서
  // 이 이미지를 참조하므로 주문마다 따로 전송하지 않는다.
  const handleSubmit = async () => {
    if (!imageFile || !delivery_id) return;

    setIsSubmitted(true);
    setIsUploading(true);

    try {
      console.log("배송 완료 이미지 전송 중...");
      const result = await completeDeliveryGroup({
        delivery_id,
        image: imageFile,
        captured_at: capturedAt || toSeoulIsoString(),
      });

      // 일부 주문의 후속 처리(에스크로 등록 등)가 실패해도 배송완료 자체는 성립한다.
      // 조용히 넘기면 정산 누락을 알 수 없으므로 기사님 화면에 남긴다.
      if (result?.failed_orders?.length) {
        console.warn("일부 주문 후속 처리 실패:", result.failed_orders);
        toast({
          title: "일부 주문 처리에 실패했습니다",
          description: `${result.failed_orders.length}건은 관리자 확인이 필요합니다.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      // 부모에게 capturedImage URL도 전달. 여기서부터 URL의 소유권은 부모에게 있다.
      if (onDelivered) {
        urlTransferredRef.current = true;
        onDelivered(delivery_id, capturedImage);
      }

      toast({
        title: "배송완료 처리 되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      // 실패 시 상태 원복
      setIsSubmitted(false);

      let msg = "전송 중 오류가 발생했습니다. 다시 시도해주세요.\n\n";
      if (error.response?.data) {
        msg += JSON.stringify(error.response?.data?.detail, null, 2) + "\n\n";
      } else {
        msg += "에러 내용: " + error.message;
      }
      if (imageFile) {
        msg += "파일 이름: " + imageFile.name + "\n";
        msg += "파일 타입: " + imageFile.type + "\n";
        msg += "파일 크기: " + imageFile.size + " 바이트\n\n";
      }

      alert(msg);
    } finally {
      setIsUploading(false);
    }
  };

  // 버튼 관련 상태 계산
  const isButtonDisabled = !isOrderConfirmed || isSubmitted;
  const buttonColor = isSubmitted
    ? "green"
    : capturedImage
    ? "green"
    : "orange";
  const handleClick =
    capturedImage && !isSubmitted ? handleSubmit : handleCameraButtonClick;

  const getButtonText = () => {
    if (isSubmitted) return "전송 완료";
    if (capturedImage) return "전송 하기";
    return "배송 사진 촬영";
  };

  return (
    <Box>
      {/* 기본 정보 영역 */}
      <Grid templateColumns="70px 1fr" my={2}>
        <GridItem>
          <Text fontSize="md">안심번호</Text>
        </GridItem>
        <GridItem>
          {phone ? (
            <Link href={`tel:${phone}`} fontSize="md" color="blue.500">
              {phone}
            </Link>
          ) : (
            <Text fontSize="md">-</Text>
          )}
        </GridItem>
      </Grid>

      <Grid templateColumns="70px 1fr" my={2}>
        <GridItem alignSelf="start">
          <Text fontSize="md">배송지</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="md">{address || "-"}</Text>
        </GridItem>
      </Grid>

      <Grid templateColumns="70px 1fr" my={2}>
        <GridItem alignSelf="start">
          <Text fontSize="md">배송메모</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="md">{content || "-"}</Text>
        </GridItem>
      </Grid>

      <Grid templateColumns="70px 1fr" my={2}>
        <GridItem alignSelf="start">
          <Text fontSize="md">비밀번호</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="md">{password || "-"}</Text>
        </GridItem>
      </Grid>

      {/* 가게 특이사항. 이전에는 이 값이 "비밀번호" 칸에 잘못 표시되고 있었다. */}
      <Grid templateColumns="70px 1fr">
        <GridItem alignSelf="start">
          <Text fontSize="md">특이사항</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="md">{note || "-"}</Text>
        </GridItem>
      </Grid>

      {/* 상품 목록 테이블 */}
      <Box
        background="white"
        width="100%"
        p={3}
        mt={4}
        borderRadius="lg"
        boxShadow="md"
        border="1px solid"
        borderColor="#E9673D"
        fontSize="md"
      >
        <TableContainer width="100%" maxHeight="250px" overflowY="auto">
          <Table variant="simple" size="md" layout="fixed" width="100%">
            <colgroup>
              <col width="80%" />
              <col width="20%" />
            </colgroup>
            <Thead>
              <Tr>
                <Th
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="gray.300"
                  fontSize="md"
                >
                  상품명
                </Th>
                <Th
                  py={3}
                  textAlign="center"
                  borderBottomWidth="1px"
                  borderColor="gray.300"
                  fontSize="md"
                >
                  수량
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {(order_items || []).map((order_item, index) => (
                <Tr key={index} borderBottomWidth="1px" borderColor="gray.100">
                  <Td py={3} whiteSpace="normal" fontSize="md">
                    <Flex align="center" gap={3}>
                      {/* 사진이 없는 상품도 이름 줄이 맞도록 같은 크기의 빈 칸을 둔다 */}
                      <Image
                        src={order_item.thumbnail || undefined}
                        alt=""
                        boxSize="44px"
                        flexShrink={0}
                        objectFit="cover"
                        borderRadius="md"
                        bg="gray.100"
                        loading="lazy"
                      />
                      <Text>{order_item.name}</Text>
                    </Flex>
                  </Td>
                  <Td py={3} textAlign="center" fontSize="md">
                    {order_item.count}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* 이미지 및 버튼 영역 */}
      <Box mt={4}>
        {(image || capturedImage) && (
          <Box mb={4} borderRadius="md" overflow="hidden" bg="gray.100">
            <Image
              src={image || capturedImage}
              alt="배송완료 사진 (탭하면 크게 보기)"
              width="100%"
              maxHeight="360px"
              // contain이어야 세로 사진이 가로로 잘려 보이지 않는다
              objectFit="contain"
              loading="lazy"
              cursor="pointer"
              onClick={() => setIsPhotoViewerOpen(true)}
            />
          </Box>
        )}

        {/* 배송 사진 전체화면 보기 */}
        <Modal isOpen={isPhotoViewerOpen} onClose={() => setIsPhotoViewerOpen(false)} size="full">
          <ModalOverlay bg="blackAlpha.900" />
          <ModalContent bg="black" m={0}>
            <ModalCloseButton color="white" zIndex={10} />
            <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" onClick={() => setIsPhotoViewerOpen(false)}>
              <Image src={image || capturedImage} alt="배송완료 사진" maxWidth="100%" maxHeight="100vh" objectFit="contain" />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* 카메라 모달 - 모든 플랫폼 지원 */}
        <Modal
          isOpen={isCameraOpen}
          onClose={() => {
            console.log("📱 인페이지 카메라 모달 닫기");
            stopCamera();
            setIsCameraOpen(false);
          }}
          size="full"
        >
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="black" m={0}>
            <ModalCloseButton color="white" zIndex={10} />
            <ModalBody
              p={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              {/* 카메라 타입 표시 */}
              <Box
                position="absolute"
                top="60px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={20}
              >
                <Text
                  color="white"
                  bg="blackAlpha.700"
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontSize="sm"
                >
                  📱 인페이지 카메라
                </Text>
              </Box>

              <Box position="relative" width="100%" height="100vh">
                {/* 비디오 스트림 */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  webkit-playsinline="true"
                  style={{
                    width: "100%",
                    height: "100%",
                    // contain이어야 카메라가 주는 프레임 전체가 화면에 보인다.
                    // cover로 잘라 보여주면 기사님이 본 화면과 저장되는 사진의
                    // 범위가 달라져, 화면 밖에 있던 영역이 사진에 들어간다.
                    objectFit: "contain",
                    backgroundColor: "#000",
                  }}
                  onLoadedMetadata={() => {
                    console.log("📱 비디오 메타데이터 로드됨");
                    if (videoRef.current) {
                      console.log(
                        "📐 비디오 실제 크기:",
                        videoRef.current.videoWidth,
                        "x",
                        videoRef.current.videoHeight
                      );
                    }
                  }}
                  onCanPlay={() => {
                    console.log("📱 비디오 재생 가능 상태");
                  }}
                  onPlay={() => {
                    console.log("📱 비디오 재생 시작");
                  }}
                  onError={(e) => {
                    console.error("📱 비디오 오류:", e);
                  }}
                />

                {/* 촬영 버튼 */}
                <Button
                  position="absolute"
                  bottom="10%"
                  left="50%"
                  transform="translateX(-50%)"
                  size="lg"
                  borderRadius="full"
                  bg="white"
                  color="black"
                  width="80px"
                  height="80px"
                  onClick={capturePhoto}
                  _hover={{ bg: "gray.100" }}
                >
                  📷
                </Button>

                {/* iOS 사용자를 위한 추가 안내 */}
                {/iPad|iPhone|iPod/.test(navigator.userAgent) && (
                  <Box
                    position="absolute"
                    bottom="120px"
                    left="50%"
                    transform="translateX(-50%)"
                    zIndex={20}
                  >
                    <Text
                      color="white"
                      bg="blackAlpha.700"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      textAlign="center"
                    >
                      🍎 iOS 최적화됨
                    </Text>
                  </Box>
                )}
              </Box>

              {/* 숨겨진 캔버스 */}
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* 숨겨진 파일 입력 */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileInput}
        />

        {/* 체크박스와 버튼 영역 */}
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          {/* 왼쪽: 체크박스 */}
          <Checkbox
            colorScheme="orange"
            size="md"
            isChecked={isOrderConfirmed}
            onChange={() => setIsOrderConfirmed(!isOrderConfirmed)}
            isDisabled={isSubmitted}
          >
            <Text fontSize="md" fontWeight="medium">
              주문 정보를 확인했습니다.
            </Text>
          </Checkbox>

          {/* 오른쪽: 버튼들 */}
          <Flex direction="column" alignItems="flex-end">
            {capturedImage && !isSubmitted && (
              <Button
                colorScheme="orange"
                size="md"
                width="140px"
                onClick={handleCameraButtonClick}
                mb={2}
                isDisabled={isSubmitted}
              >
                배송 사진 재촬영
              </Button>
            )}
            <Button
              colorScheme={buttonColor}
              size="md"
              width="140px"
              isDisabled={isButtonDisabled}
              opacity={isButtonDisabled ? 0.6 : 1}
              onClick={handleClick}
            >
              {getButtonText()}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default DeliveryDetail;
